import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { GraduationCap, Trophy, Shield, Zap, Check, ArrowRight } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <GraduationCap className="w-8 h-8 text-indigo-400" />,
      title: "Learn DeFi Risk-Free",
      description: "Master staking, borrowing, and yield strategies on Sepolia testnet with zero financial risk"
    },
    {
      icon: <Trophy className="w-8 h-8 text-yellow-400" />,
      title: "Compete for Real Prizes",
      description: "Join weekly competitions and earn real USD rewards while learning with test tokens"
    },
    {
      icon: <Shield className="w-8 h-8 text-green-400" />,
      title: "Blockchain Certificates",
      description: "Earn verifiable credentials stored on-chain to boost your crypto career"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-400" />,
      title: "Hands-On Practice",
      description: "Real smart contracts, real transactions, real learning - just with test ETH"
    }
  ];

  const tiers = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: [
        "DeFi Basics Course",
        "1,000 test BETH tokens",
        "5 practice transactions/day",
        "Community access"
      ],
      cta: "Start Learning",
      variant: "outline"
    },
    {
      name: "Pro",
      price: "$29",
      period: "month",
      popular: true,
      features: [
        "All Free features",
        "Unlimited test tokens",
        "Weekly competitions ($100 prizes)",
        "Monthly tournaments ($500 prizes)",
        "DeFi Fundamentals Certificate",
        "Priority support"
      ],
      cta: "Go Pro",
      variant: "default"
    },
    {
      name: "Premium",
      price: "$99",
      period: "month",
      features: [
        "All Pro features",
        "Advanced DeFi modules",
        "2hrs/month 1-on-1 mentorship",
        "Advanced Strategist Certificate",
        "Early mainnet airdrop (2x points)",
        "API access for developers"
      ],
      cta: "Get Premium",
      variant: "default"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10" />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="flex justify-center mb-6">
            <Badge variant="secondary" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30">
              🎓 Educational Platform • Sepolia Testnet
            </Badge>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-center mb-6 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Master DeFi Risk-Free
          </h1>
          
          <p className="text-xl md:text-2xl text-center text-gray-300 mb-8 max-w-3xl mx-auto">
            <span className="block text-green-400 font-bold mb-2">✓ LEGITIMATE EDUCATIONAL PLATFORM</span>
            Learn decentralized finance through hands-on practice on Ethereum Sepolia testnet.
            <span className="block text-indigo-400 font-semibold mt-2">No real money. Real learning. Real rewards.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8 py-6"
              onClick={() => navigate('/register')}
            >
              Start Learning Free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-8 py-6 border-indigo-500/50 hover:bg-indigo-500/10"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>

          <div className="mt-12 flex justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Practice with Test Tokens</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Earn Real Prizes</span>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/10 border border-green-500/30 rounded-full text-sm text-green-400">
              <Shield className="w-5 h-5" />
              <span className="font-semibold">Safe Educational Platform • Testnet Only</span>
            </div>
            <div className="flex gap-4 text-xs text-gray-400">
              <a href="/about" className="hover:text-green-400 underline">Why We're Safe</a>
              <span>•</span>
              <a href="/terms" className="hover:text-green-400 underline">Terms of Service</a>
              <span>•</span>
              <a href="/privacy" className="hover:text-green-400 underline">Privacy Policy</a>
              <span>•</span>
              <a href="https://sepolia.etherscan.io/address/0x93E216E4FF0336737BfC61ddABA6712F543b4770" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 underline">Verify Contracts</a>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Why Bethereum Academy?
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, idx) => (
            <Card key={idx} className="bg-white/5 border-white/10 hover:border-indigo-500/50 transition-all hover:scale-105">
              <CardHeader>
                <div className="mb-4">{feature.icon}</div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-400">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Pricing Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Choose Your Learning Path
          </h2>
          <p className="text-xl text-gray-400">
            Start free, upgrade when you're ready to accelerate your learning
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier, idx) => (
            <Card 
              key={idx} 
              className={`relative ${
                tier.popular 
                  ? 'bg-gradient-to-b from-indigo-600/20 to-purple-600/20 border-indigo-500 scale-105' 
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-indigo-600 text-white">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{tier.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  <span className="text-gray-400">/{tier.period}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  variant={tier.variant}
                  onClick={() => navigate('/register')}
                >
                  {tier.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-2xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Master DeFi?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of students learning blockchain without risking real money
          </p>
          <Button 
            size="lg" 
            className="bg-indigo-600 hover:bg-indigo-700 text-lg px-12 py-6"
            onClick={() => navigate('/register')}
          >
            Get Started Free
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Home;
